import * as THREE from 'three';
import { BLACK, DSTEEL, basic, cloth, emis, leather, metal, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Portaestandarte de hueso (docs/CRIATURAS.md §2): era II support. A dead knight of the fallen garrison, taller
 * and heavier than the ash skeletons: rusted half-plate over bare ribs, a cracked helm with a ragged plume, a
 * notched sword in the right fist and, in the left, the war banner of the nightmare on a pole twice its height:
 * black cloth under an ink-violet header, torn through, with a pale keep turned upside down and dripping (the
 * titan's dream mocking Somnhold), a skull on the finial and trophies chained to the crossbar. The banner is the
 * silhouette; an ink pool at its feet says it lends its strength to the pack.
 *
 * Opaque shared materials; the ember (eyes, the coal in its chest) is its one self-lit material and the ink (the
 * pool and the smoke off it) its one translucent one, plus a few glow sprites.
 */

const UP = V(0, 1, 0);

/** The pole: where the left fist grips it, its butt and its top, the crossbar's height and the cloth's span. */
export const BANNER = {
  fist: V(-0.44, 1.22, 0.2),
  butt: 0.1,
  top: 3.5,
  bar: 3.34,
  /** Cloth bands (top edges): header, field (carries the sigil), tails. */
  bands: [3.32, 3.0, 2.52] as const,
  low: 2.17,
  width: 0.84,
};

/** Fold of the cloth across its width (shared by every band, so the seams meet at rest). */
const fold = (x: number): number => 0.022 * Math.sin(x * 11 + 0.6) + 0.01 * Math.sin(x * 23 + 1.9);

/**
 * A band of torn cloth hanging from its top edge (y = 0) down to -h, across `w`, folded in z. `holes` are grid
 * cells left out (torn through); the hem is ragged, or cut into points when `tails` is set.
 */
const band = (w: number, h: number, cols: number, rows: number, rng: () => number, holes: readonly [number, number][] = [], tails = 0): THREE.BufferGeometry => {
  const pos: number[] = [];
  const idx: number[] = [];
  for (let j = 0; j <= rows; j++) {
    for (let i = 0; i <= cols; i++) {
      let x = -w / 2 + (i / cols) * w;
      let y = -(j / rows) * h;
      // Frayed side edges, a ragged hem (or long points).
      if (i === 0 || i === cols) x += (rng() - 0.5) * 0.03 * (j > 0 ? 1 : 0);
      // Inner vertices wander, so the torn holes come out ragged rather than square.
      else if (j > 0 && j < rows) {
        x += (rng() - 0.5) * (w / cols) * 0.45;
        y += (rng() - 0.5) * (h / rows) * 0.4;
      }
      if (j === rows) y -= tails > 0 ? (i % 2 ? tails * (0.75 + rng() * 0.5) : rng() * 0.04) : rng() * 0.05;
      pos.push(x, y, fold(x) * (0.6 + 0.4 * (j / rows)));
    }
  }
  const skip = new Set(holes.map(([c, r]) => r * cols + c));
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      if (skip.has(j * cols + i)) continue;
      const a = j * (cols + 1) + i, b = a + 1, c = a + cols + 1, d = c + 1;
      idx.push(a, c, b, b, c, d);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
};

/** The sigil: Somnhold's keep, upside down, its battlements turned into drips. Spans [-0.5, 0.5] wide, y 0 (top) to -1. */
const keepSigil = (): THREE.Shape => {
  // Upright outline (y up from the base), then flipped.
  const up: [number, number][] = [
    [-0.45, 0], [-0.45, 0.63], [-0.4, 0.63], [-0.4, 0.57], [-0.32, 0.57], [-0.32, 0.63], [-0.27, 0.63], [-0.27, 0.4], [-0.14, 0.4], [-0.14, 0.75],
    [-0.19, 0.75], [0, 1.08], [0.19, 0.75], [0.14, 0.75], [0.14, 0.4], [0.27, 0.4], [0.27, 0.63], [0.32, 0.63], [0.32, 0.57], [0.4, 0.57],
    [0.4, 0.63], [0.45, 0.63], [0.45, 0], [0.08, 0], [0.08, 0.15], [0, 0.22], [-0.08, 0.15], [-0.08, 0],
  ];
  // Upside down, the battlements and the spire run: ink drips off them.
  const drip: Record<number, number> = { 1: 0.1, 2: 0.12, 5: 0.2, 6: 0.17, 11: 0.2, 16: 0.08, 17: 0.07, 20: 0.24, 21: 0.21 };
  const s = new THREE.Shape();
  up.forEach(([x, y], i) => {
    const yy = -(y + (drip[i] ?? 0)) / 1.28;
    if (i === 0) s.moveTo(x, yy);
    else s.lineTo(x, yy);
  });
  s.closePath();
  // The spire's window slit, and a crack splitting the left tower.
  const hole = new THREE.Path();
  hole.moveTo(-0.03, -0.36);
  hole.lineTo(0.03, -0.36);
  hole.lineTo(0.03, -0.48);
  hole.lineTo(-0.03, -0.48);
  hole.closePath();
  const crack = new THREE.Path();
  crack.moveTo(-0.33, -0.04);
  crack.lineTo(-0.38, -0.16);
  crack.lineTo(-0.33, -0.27);
  crack.lineTo(-0.38, -0.42);
  crack.lineTo(-0.35, -0.28);
  crack.lineTo(-0.4, -0.16);
  crack.closePath();
  s.holes.push(hole, crack);
  return s;
};

/**
 * Bones: hips (pelvis, tassets; the tabard flap on `tabard`), chest above them carrying the head (the jaw hinged
 * under the helm, the `plume` trailing from its crest) and both arms. The left fist grips the `pole`; the cloth
 * hangs from the crossbar in three bands chained one under the other (`bannerA` header, `bannerB` field with the
 * sigil, `bannerC` tails) so clips can make it billow and snap, and a trophy chain swings from each end of the
 * crossbar (`trophyL`, `trophyR`). The right fist holds the `sword`. Legs bend at the knee. The ink pool at its
 * feet is the `aura` (clips gutter it out when it dies).
 */
export const bannerman = (o?: BaseOpts): Model => {
  const c = begin(o, 0.7, 47);
  const { g, rng } = c;
  const bone = std('#b4afa6', { roughness: 0.8, bumpScale: 0.03 });
  const boneD = std('#6f6a63', { roughness: 0.9, bumpScale: 0.03 });
  const iron = metal('#6a6870', { roughness: 0.5, bumpScale: 0.05, side: THREE.DoubleSide });
  const rust = std('#4f2c20', { roughness: 0.95, flatShading: true, bumpScale: 0.07 });
  const wood = std('#241e1b', { roughness: 0.95, flatShading: true, bumpScale: 0.06 });
  const strap = leather('#211a18');
  const field = cloth('#141119', { side: THREE.DoubleSide, bumpScale: 0.04 });
  const header = cloth('#2c2141', { side: THREE.DoubleSide, bumpScale: 0.04 });
  const pale = std('#c3c1ca', { roughness: 0.9, side: THREE.DoubleSide, bumpScale: 0.02 });
  const plumeM = cloth('#511915', { side: THREE.DoubleSide });
  const ember = emis('#ff8a6e', '#ff4f3a', 1.7);
  const ink = basic('#2b2244', 0.42);

  const SH = [-1, 1].map((k) => V(k * 0.25, 1.62, -0.01));
  const EL = [V(-0.34, 1.33, 0.0), V(0.33, 1.31, 0.03)];
  const WR = [BANNER.fist, V(0.37, 1.02, 0.15)];
  const auraJ = joint(g, V(0, 0.01, 0), 'aura');
  const hipsJ = joint(g, V(0, 1.0, 0), 'hips');
  const hp = hipsJ.abs;
  const tabJ = joint(hp, V(0, 1.0, 0.17), 'tabard');
  const chestJ = joint(hp, V(0, 1.1, -0.01), 'chest');
  const ch = chestJ.abs;
  const headJ = joint(ch, V(0, 1.72, 0.0), 'head');
  const jawJ = joint(headJ.abs, V(0, 1.78, 0.02), 'jaw');
  const plumeJ = joint(headJ.abs, V(0, 2.03, -0.06), 'plume');
  const armJ = [-1, 1].map((k, i) => joint(ch, SH[i]!, k < 0 ? 'armL' : 'armR'));
  const foreJ = [-1, 1].map((k, i) => joint(armJ[i]!.abs, EL[i]!, k < 0 ? 'foreL' : 'foreR'));
  const poleJ = joint(foreJ[0]!.abs, BANNER.fist, 'pole');
  const pl = poleJ.abs;
  const PX = BANNER.fist.x, PZ = BANNER.fist.z;
  const BZ = PZ + 0.045;
  const bandJ: { p: THREE.Group; abs: THREE.Group }[] = [];
  let parent: THREE.Object3D = pl;
  for (const [i, y] of BANNER.bands.entries()) {
    const j = joint(parent, V(PX, y, BZ), `banner${'ABC'[i]}`);
    bandJ.push(j);
    parent = j.abs;
  }
  const BW = BANNER.width;
  const barEnds = [PX - BW / 2 - 0.08, PX + BW / 2 + 0.08];
  const trophyJ = [-1, 1].map((k, i) => joint(pl, V(barEnds[i]!, BANNER.bar - 0.02, PZ), k < 0 ? 'trophyL' : 'trophyR'));
  const swordJ = joint(foreJ[1]!.abs, WR[1]!, 'sword');
  const legJ = [-1, 1].map((k) => joint(hp, V(k * 0.12, 0.97, 0), k < 0 ? 'legL' : 'legR'));
  const shinJ = [-1, 1].map((k, i) => joint(legJ[i]!.abs, V(k * 0.13, 0.55, 0.03), k < 0 ? 'shinL' : 'shinR'));

  /** A squashed sphere. */
  const lump = (p: THREE.Object3D, mat: THREE.Material, r: number, x: number, y: number, z: number, sx = 1, sy = 1, sz = 1, seg = 7): THREE.Mesh => {
    const m = M(new THREE.SphereGeometry(r, seg, Math.max(4, seg - 3)), mat, p);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    return m;
  };
  /** A plate shell: part of a sphere facing +Z (or `phi0`), thetas limit it top to bottom. */
  const shell = (p: THREE.Object3D, mat: THREE.Material, r: number, phi: [number, number], theta: [number, number], seg = 9): THREE.Mesh =>
    M(new THREE.SphereGeometry(r, seg, 4, phi[0], phi[1], theta[0], theta[1]), mat, p);
  /** A tattered strip of cloth from `a` hanging toward `dir`, tapering, curling by `curl` along its length. */
  const rag = (p: THREE.Object3D, mat: THREE.Material, a: THREE.Vector3, dir: THREE.Vector3, len: number, w: number, curl: number, side = V(1, 0, 0)): void => {
    const gm = new THREE.PlaneGeometry(1, 1, 1, 4);
    const ps = gm.attributes.position as THREE.BufferAttribute;
    const d = dir.clone().normalize();
    const nrm = new THREE.Vector3().crossVectors(side, d).normalize();
    for (let i = 0; i < ps.count; i++) {
      const u = 0.5 - ps.getY(i); // 0 at the root, 1 at the tip
      const s = ps.getX(i) * w * (1 - 0.75 * u) + (u > 0.95 ? (rng() - 0.5) * 0.02 : 0);
      const q = a.clone().addScaledVector(d, u * len).addScaledVector(side, s).addScaledVector(nrm, curl * u * u);
      ps.setXYZ(i, q.x, q.y, q.z);
    }
    gm.computeVertexNormals();
    M(gm, mat, p);
  };

  // The ink pool at its feet: a torn ring of ink on the ground, smoke boiling off its rim, a violet glow.
  const pool = new THREE.RingGeometry(0.18, 0.8, 18, 1);
  const pp = pool.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pp.count; i++) {
    const x = pp.getX(i), y = pp.getY(i), r = Math.hypot(x, y);
    if (r > 0.5) {
      const k = (0.62 + rng() * 0.45) / r * 0.8;
      pp.setXY(i, x * k, y * k);
    }
  }
  const poolM = M(pool, ink, auraJ.abs);
  poolM.rotation.x = -Math.PI / 2;
  poolM.position.y = 0.012;
  const auraGlow = glowSprite(auraJ.abs, '#6a3a9a', 2.2, V(0, 0.12, 0), 0.45, true);
  const ribs: Ribbon[] = [];
  for (let i = 0; i < 2; i++) {
    const a = 0.9 + i * 3.3;
    ribs.push(new Ribbon(auraJ.abs, wisp(Math.cos(a) * 0.55, Math.sin(a) * 0.55, 0.55, 5), 0.03, ink, rng, 0.06));
  }

  // Legs: bare bone above the knee, iron below: knee cops, greaves open at the back, pointed sabatons.
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const th = legJ[i]!.abs, sh = shinJ[i]!.abs;
    const hip = V(k * 0.12, 0.97, 0), knee = V(k * 0.13, 0.55, 0.03), ank = V(k * 0.12, 0.11, -0.01);
    limb(th, hip, knee, 0.03, 0.026, bone, 6);
    lump(th, bone, 0.045, hip.x, hip.y, hip.z, 1, 1, 1, 6);
    const cop = shell(sh, iron, 0.075, [Math.PI * 0.1, Math.PI * 0.8], [0, Math.PI * 0.6], 7);
    cop.position.copy(knee).add(V(0, 0, 0.02));
    cop.rotation.x = Math.PI / 2;
    limb(sh, knee, ank, 0.024, 0.022, bone, 5);
    const gr = M(new THREE.CylinderGeometry(0.07, 0.058, 0.36, 8, 1, true, -Math.PI * 0.62, Math.PI * 1.24), iron, sh);
    gr.position.set(k * 0.125, 0.31, 0.02);
    gr.rotation.x = -0.05;
    // Rust bleeding down from the knee, and a dent.
    const rs = M(new THREE.BoxGeometry(0.05, 0.16, 0.02), rust, sh);
    rs.position.set(k * 0.15, 0.38, 0.085);
    rs.rotation.set(-0.05, k * 0.4, 0);
    const sab = M(new THREE.CylinderGeometry(0.03, 0.065, 0.24, 6, 1), iron, sh);
    sab.position.set(k * 0.125, 0.05, 0.06);
    sab.rotation.x = Math.PI / 2;
    sab.scale.set(1.1, 1, 0.55);
    M(new THREE.BoxGeometry(0.11, 0.06, 0.1), iron, sh).position.set(k * 0.12, 0.05, -0.04);
  }

  // Pelvis: a ring of bone under hanging tassets, a sword belt, and the ragged tabard.
  const pel = M(new THREE.TorusGeometry(0.11, 0.035, 4, 8), bone, hp);
  pel.position.set(0, 0.98, 0);
  pel.rotation.x = Math.PI / 2;
  pel.scale.set(1.2, 1, 0.8);
  const belt = M(new THREE.TorusGeometry(0.17, 0.025, 3, 12), strap, hp);
  belt.position.set(0, 1.07, 0);
  belt.rotation.x = Math.PI / 2 + 0.08;
  belt.scale.set(1.05, 0.85, 1);
  M(new THREE.BoxGeometry(0.07, 0.06, 0.03), iron, hp).position.set(0, 1.06, 0.15);
  for (const k of [-1, 1]) {
    const ts = M(new THREE.CylinderGeometry(0.17, 0.22, 0.2, 6, 1, true, k < 0 ? -Math.PI * 0.95 : Math.PI * 0.1, Math.PI * 0.75), iron, hp);
    ts.position.set(k * 0.02, 0.96, 0);
    ts.rotation.y = 0.05 * k;
  }
  rag(tabJ.abs, header, V(0, 1.04, 0.18), V(0, -1, 0.12), 0.56, 0.26, 0.04);
  rag(hp, field, V(0, 1.04, -0.17), V(0, -1, -0.1), 0.48, 0.3, -0.04);

  // Spine and ribs, bare behind the breastplate; a coal glowing between them.
  for (let i = 0; i < 4; i++) lump(ch, bone, 0.034, 0, 1.13 + i * 0.1, -0.07, 1, 0.9, 1, 5);
  limb(ch, V(0, 1.1, -0.07), V(0, 1.62, -0.06), 0.02, 0.02, bone, 4);
  for (let i = 0; i < 4; i++) {
    const rib = M(new THREE.TorusGeometry(0.15 - i * 0.012, 0.014, 3, 7, Math.PI * 1.25), bone, ch);
    rib.rotation.set(Math.PI / 2 + 0.15, 0, Math.PI * 0.12);
    rib.position.set(0, 1.5 - i * 0.075, -0.03);
  }
  lump(ch, ember, 0.05, 0.02, 1.32, -0.02, 1, 1.2, 1, 6);
  const heart = glowSprite(ch, '#ff4f3a', 0.45, V(0.02, 1.32, 0.0), 0.35, true);
  // The breastplate: a rusted shell over the front of the ribs, with a ridge, a torn gash (the coal shows
  // through) and a gorget.
  const bp = shell(ch, iron, 0.24, [Math.PI * 0.12, Math.PI * 0.76], [Math.PI * 0.18, Math.PI * 0.5], 10);
  bp.position.set(0, 1.4, -0.05);
  bp.scale.set(1, 1.25, 0.85);
  M(new THREE.BoxGeometry(0.025, 0.3, 0.03), iron, ch).position.set(0, 1.42, 0.17);
  const gash = M(new THREE.BoxGeometry(0.035, 0.13, 0.03), BLACK, ch);
  gash.position.set(0.08, 1.33, 0.165);
  gash.rotation.set(0.15, 0.3, 0.45);
  M(new THREE.BoxGeometry(0.014, 0.08, 0.02), ember, ch).position.set(0.08, 1.33, 0.18);
  // Rust weeping down the plate from its rivets and dents: streaks laid on the shell's curve.
  for (const [x, y, l] of [[-0.11, 1.47, 0.12], [-0.06, 1.52, 0.08], [0.13, 1.47, 0.1], [-0.12, 1.3, 0.07]] as const) {
    const zz = -0.05 + 0.85 * 0.24 * Math.sqrt(Math.max(0, 1 - (x / 0.24) ** 2 - ((y - 1.4) / 0.3) ** 2));
    const st = M(new THREE.BoxGeometry(0.022, l, 0.012), rust, ch);
    st.position.set(x, y - l / 2, zz + 0.004);
    st.rotation.set(-0.35, Math.atan2(x, 0.2), 0);
    M(new THREE.BoxGeometry(0.018, 0.018, 0.012), DSTEEL, ch).position.set(x, y, zz + 0.008);
  }
  const gor = M(new THREE.CylinderGeometry(0.09, 0.15, 0.1, 9, 1, true), iron, ch);
  gor.position.set(0, 1.66, 0.0);
  // Bands round the spine at the waist (the fauld, half gone).
  const fauld = M(new THREE.CylinderGeometry(0.16, 0.17, 0.08, 8, 1, true, -Math.PI * 0.45, Math.PI * 0.9), iron, ch);
  fauld.position.set(0, 1.16, 0);
  // A strap across from the right shoulder to the left hip, where the banner's cup hangs.
  limb(ch, V(0.2, 1.64, 0.08), V(-0.17, 1.12, 0.17), 0.02, 0.02, strap, 4).scale.set(1.6, 1, 0.5);

  // Pauldrons: layered lames, the left one heavier with a raised guard against the neck.
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const up = armJ[i]!.abs;
    const s = SH[i]!;
    const big = k < 0 ? 1.18 : 1;
    for (let l = 0; l < 2; l++) {
      const pd = shell(up, iron, 0.13 * big - l * 0.015, [0, Math.PI * 2], [0, Math.PI * 0.42], 8);
      pd.position.set(s.x + k * (0.03 + l * 0.03), s.y + 0.03 - l * 0.08, s.z);
      pd.rotation.z = -k * (0.55 + l * 0.25);
      pd.scale.set(1, 0.8, 1.05);
    }
    if (k < 0) {
      const guard = M(new THREE.BoxGeometry(0.02, 0.12, 0.22), iron, up);
      guard.position.set(s.x + 0.06, s.y + 0.14, s.z);
      guard.rotation.z = -0.25;
      for (let j = 0; j < 2; j++) {
        const sp = M(new THREE.ConeGeometry(0.02, 0.12, 4), iron, up);
        sp.position.set(s.x - 0.1, s.y + 0.1, s.z - 0.05 + j * 0.1);
        sp.rotation.z = 0.7;
      }
    }
    const rr = M(new THREE.BoxGeometry(0.06, 0.05, 0.12), rust, up);
    rr.position.set(s.x + k * 0.1, s.y - 0.02, s.z + 0.02);
    rr.rotation.z = -k * 0.7;
  }

  // Arms: bare humerus, an iron vambrace on the forearm, a gauntlet.
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const up = armJ[i]!.abs, fo = foreJ[i]!.abs;
    const s = SH[i]!, e = EL[i]!, w = WR[i]!;
    limb(up, s, e, 0.026, 0.024, bone, 5);
    lump(up, bone, 0.036, e.x, e.y, e.z, 1, 1, 1, 5);
    limb(fo, e, w, 0.022, 0.02, bone, 5);
    const vb = limb(fo, e.clone().lerp(w, 0.3), e.clone().lerp(w, 0.85), 0.052, 0.045, iron, 7);
    vb.scale.z = 0.95;
    const gl = M(new THREE.BoxGeometry(0.08, 0.085, 0.1), iron, fo);
    gl.position.copy(w);
    gl.rotation.y = k * 0.3;
  }

  // Head: a skull in a cracked great helm (a T-slit with ember eyes burning in it), the jaw hanging below the
  // cheek guards, a crest ridge and the ragged plume.
  const hd = headJ.abs;
  lump(hd, bone, 0.105, 0, 1.84, 0.02, 0.92, 1.0, 1.0, 7);
  const helm = lump(hd, iron, 0.145, 0, 1.88, 0.0, 1.0, 1.12, 1.08, 10);
  helm.rotation.x = -0.08;
  for (const k of [-1, 1]) {
    const cheek = M(new THREE.BoxGeometry(0.02, 0.12, 0.13), iron, hd);
    cheek.position.set(k * 0.12, 1.76, 0.06);
    cheek.rotation.set(0.15, -k * 0.35, k * 0.12);
  }
  M(new THREE.BoxGeometry(0.2, 0.03, 0.04), BLACK, hd).position.set(0, 1.9, 0.14);
  M(new THREE.BoxGeometry(0.045, 0.14, 0.04), BLACK, hd).position.set(0, 1.82, 0.145);
  for (const k of [-1, 1]) {
    M(new THREE.BoxGeometry(0.03, 0.016, 0.01), ember, hd).position.set(k * 0.045, 1.9, 0.163);
    glowSprite(hd, '#ff4f3a', 0.13, V(k * 0.045, 1.9, 0.18), 0.95);
  }
  const crest = M(new THREE.BoxGeometry(0.025, 0.06, 0.3), iron, hd);
  crest.position.set(0, 2.03, -0.01);
  crest.rotation.x = -0.15;
  // The crack: the iron split from the brow over the right temple, the skull showing in the gap.
  const gap = lump(hd, bone, 0.04, 0.1, 1.95, 0.08, 0.6, 1.2, 0.7, 5);
  gap.rotation.z = -0.5;
  const crk = M(new THREE.BoxGeometry(0.012, 0.13, 0.02), BLACK, hd);
  crk.position.set(0.075, 1.97, 0.12);
  crk.rotation.set(0.3, 0.4, -0.55);
  const rh = M(new THREE.BoxGeometry(0.08, 0.04, 0.02), rust, hd);
  rh.position.set(-0.08, 1.82, 0.13);
  rh.rotation.y = -0.5;
  const jw = M(new THREE.BoxGeometry(0.11, 0.035, 0.09), bone, jawJ.abs);
  jw.position.set(0, 1.72, 0.06);
  for (let t = 0; t < 4; t++) M(new THREE.BoxGeometry(0.014, 0.022, 0.012), bone, jawJ.abs).position.set(-0.036 + t * 0.024, 1.745, 0.1);
  // The plume: dark red rags from the crest, streaming back.
  for (let p = 0; p < 5; p++) {
    const a = (p - 2) * 0.16;
    rag(plumeJ.abs, plumeM, V(a * 0.08, 2.05, 0.04 - p * 0.035), V(a, 0.25 - p * 0.05, -1), 0.38 + 0.07 * Math.sin(p * 2.3), 0.1, -0.12, V(1, 0, 0));
  }

  // The sword: notched, the edge rusted, the crossguard bent; it hangs from the right fist toward the ground.
  const sw = G(swordJ.abs, WR[1]!.x, WR[1]!.y, WR[1]!.z);
  sw.quaternion.setFromUnitVectors(UP, V(0.05, -0.9, 0.42).normalize());
  const blade = new THREE.Shape();
  blade.moveTo(-0.03, 0);
  blade.lineTo(-0.03, 0.3);
  blade.lineTo(-0.018, 0.33);
  blade.lineTo(-0.03, 0.36);
  blade.lineTo(-0.028, 0.66);
  blade.lineTo(0, 0.78);
  blade.lineTo(0.026, 0.62);
  blade.lineTo(0.016, 0.55);
  blade.lineTo(0.028, 0.52);
  blade.lineTo(0.03, 0);
  blade.closePath();
  const bg = new THREE.ExtrudeGeometry(blade, { depth: 0.012, bevelEnabled: false, curveSegments: 2 });
  bg.translate(0, 0.09, -0.006);
  M(bg, metal('#5b5d63', { roughness: 0.6, bumpScale: 0.05 }), sw);
  const guard = M(new THREE.BoxGeometry(0.2, 0.025, 0.035), iron, sw);
  guard.position.y = 0.085;
  guard.rotation.z = 0.12;
  M(new THREE.CylinderGeometry(0.016, 0.016, 0.14, 5), strap, sw).position.y = 0.01;
  lump(sw, iron, 0.028, 0, -0.07, 0, 1, 1, 1, 5);

  // The pole: dark wood bound in iron, an iron-shod butt, a skull on the finial with an iron spike through it.
  limb(pl, V(PX, BANNER.butt + 0.1, PZ), V(PX, BANNER.top, PZ), 0.03, 0.026, wood, 6);
  const shoe = M(new THREE.ConeGeometry(0.035, 0.16, 5), iron, pl);
  shoe.position.set(PX, BANNER.butt + 0.07, PZ);
  shoe.rotation.x = Math.PI;
  for (const y of [0.5, 1.45, 2.3]) {
    const bd = M(new THREE.CylinderGeometry(0.036, 0.036, 0.05, 6), iron, pl);
    bd.position.set(PX, y, PZ);
  }
  // A cup strapped at the hip once took the butt; now the fist alone holds it. The fist wraps the pole.
  M(new THREE.BoxGeometry(0.075, 0.1, 0.09), iron, pl).position.set(PX, BANNER.fist.y + 0.02, PZ);
  // The crossbar, iron-capped, and the finial.
  const bar = M(new THREE.CylinderGeometry(0.022, 0.022, BW + 0.2, 5), wood, pl);
  bar.rotation.z = Math.PI / 2;
  bar.position.set(PX, BANNER.bar, PZ);
  for (const x of barEnds) {
    const cap = M(new THREE.ConeGeometry(0.028, 0.08, 4), iron, pl);
    cap.position.set(x + Math.sign(x - PX) * 0.05, BANNER.bar, PZ);
    cap.rotation.z = -Math.sign(x - PX) * Math.PI / 2;
  }
  M(new THREE.CylinderGeometry(0.04, 0.03, 0.06, 6), iron, pl).position.set(PX, BANNER.top - 0.02, PZ);
  const sk = G(pl, PX, BANNER.top + 0.1, PZ + 0.01);
  sk.rotation.set(0.25, 0, 0.08);
  lump(sk, bone, 0.085, 0, 0, 0, 0.92, 1, 1.05, 7);
  M(new THREE.BoxGeometry(0.075, 0.04, 0.06), bone, sk).position.set(0, -0.07, 0.04);
  for (const k of [-1, 1]) {
    lump(sk, BLACK, 0.024, k * 0.032, 0.0, 0.07, 1, 0.9, 0.5, 4);
    M(new THREE.BoxGeometry(0.014, 0.01, 0.006), ember, sk).position.set(k * 0.032, 0.0, 0.082);
    glowSprite(sk, '#ff4f3a', 0.1, V(k * 0.032, 0.0, 0.09), 0.85);
  }
  const spike = M(new THREE.ConeGeometry(0.018, 0.2, 4), iron, sk);
  spike.position.y = 0.13;
  for (let s = 0; s < 3; s++) {
    // A crown of bent iron spikes under the skull.
    const a = s * 2.1 + 0.4;
    const cs = M(new THREE.ConeGeometry(0.012, 0.14, 4), iron, pl);
    cs.position.set(PX + Math.cos(a) * 0.045, BANNER.top + 0.02, PZ + Math.sin(a) * 0.045);
    cs.quaternion.setFromUnitVectors(UP, V(Math.cos(a), 0.6, Math.sin(a)).normalize());
  }

  // The banner: three bands hung from the crossbar, one under the other.
  const [yA, yB, yC] = BANNER.bands;
  const hA = yA - yB, hB = yB - yC, hC = yC - BANNER.low;
  const bandA = M(band(BW, hA, 6, 2, rng, [[5, 0]]), header, bandJ[0]!.abs);
  bandA.position.set(PX, yA, BZ);
  const bandB = M(band(BW, hB, 6, 3, rng, [[0, 2], [5, 1]]), field, bandJ[1]!.abs);
  bandB.position.set(PX, yB, BZ);
  const bandC = M(band(BW, hC, 7, 2, rng, [[2, 0], [6, 1]], 0.2), field, bandJ[2]!.abs);
  bandC.position.set(PX, yC, BZ);
  // The header's top is wrapped round the crossbar; a fringe of rings binds it.
  for (let r = 0; r < 4; r++) {
    const ring = M(new THREE.TorusGeometry(0.03, 0.009, 3, 5), iron, bandJ[0]!.abs);
    ring.position.set(PX - BW / 2 + 0.08 + r * ((BW - 0.16) / 3), yA + 0.015, BZ - 0.02);
  }
  // The sigil, painted on the field and following its folds (both faces: it shows through the cloth).
  const sg = new THREE.ShapeGeometry(keepSigil(), 3);
  const sgp = sg.attributes.position as THREE.BufferAttribute;
  const SW = 0.64, SY = yB - 0.05;
  for (let i = 0; i < sgp.count; i++) {
    const x = sgp.getX(i) * SW, y = sgp.getY(i) * SW;
    sgp.setXYZ(i, x, y, fold(x) * (0.6 + 0.4 * Math.min(1, -y / hB)) + 0.01);
  }
  sg.computeVertexNormals();
  M(sg, pale, bandJ[1]!.abs).position.set(PX, SY, BZ);
  // A slash of the same pale paint along the header: the old ward line, crossed out.
  const ln = M(new THREE.BoxGeometry(BW * 0.8, 0.02, 0.004), pale, bandJ[0]!.abs);
  ln.position.set(PX, yA - hA * 0.62, BZ + fold(0) + 0.008);

  // Trophies: from the left end a chain with a skull; from the right a broken shackle and a thighbone.
  const chain = (p: THREE.Object3D, x: number, y: number, n: number): number => {
    for (let i = 0; i < n; i++) {
      const lk = M(new THREE.TorusGeometry(0.026, 0.008, 3, 4), iron, p);
      lk.position.set(x, y - 0.03 - i * 0.045, PZ);
      lk.rotation.y = i % 2 ? Math.PI / 2 : 0;
      lk.scale.y = 1.35;
    }
    return y - 0.03 - n * 0.045;
  };
  const tL = trophyJ[0]!.abs, tR = trophyJ[1]!.abs;
  const yl = chain(tL, barEnds[0]!, BANNER.bar - 0.02, 5);
  const sk2 = G(tL, barEnds[0]!, yl - 0.05, PZ);
  sk2.rotation.set(0.3, 0.4, 0.2);
  lump(sk2, bone, 0.06, 0, 0, 0, 0.92, 1, 1.05, 6);
  for (const k of [-1, 1]) lump(sk2, BLACK, 0.017, k * 0.022, 0.005, 0.05, 1, 0.9, 0.5, 4);
  M(new THREE.BoxGeometry(0.05, 0.03, 0.04), bone, sk2).position.set(0, -0.05, 0.03);
  const yr = chain(tR, barEnds[1]!, BANNER.bar - 0.02, 3);
  const sh = M(new THREE.TorusGeometry(0.045, 0.012, 3, 8, Math.PI * 1.5), iron, tR);
  sh.position.set(barEnds[1]!, yr - 0.03, PZ);
  sh.rotation.z = Math.PI * 0.25;
  limb(tR, V(barEnds[1]! - 0.02, yr - 0.06, PZ), V(barEnds[1]! + 0.03, yr - 0.32, PZ + 0.02), 0.014, 0.012, boneD, 5);
  lump(tR, boneD, 0.025, barEnds[1]! + 0.03, yr - 0.33, PZ + 0.02, 1.3, 1, 1, 5);

  // Only the boil here: every move is the clips' (anim/creatures/bannerman.ts).
  const t = (n: number): void => {
    for (const r of ribs) r.step();
    heart.material.opacity = 0.28 + ((n * 3) % 7) / 40;
    auraGlow.material.opacity = 0.38 + ((n * 5) % 4) / 50;
  };
  t(0);
  return done(c, 2.2, t, undefined, bonesOf(c, {
    aura: auraJ.p, hips: hipsJ.p, tabard: tabJ.p, chest: chestJ.p, head: headJ.p, jaw: jawJ.p, plume: plumeJ.p,
    armL: armJ[0]!.p, foreL: foreJ[0]!.p, pole: poleJ.p, bannerA: bandJ[0]!.p, bannerB: bandJ[1]!.p, bannerC: bandJ[2]!.p,
    trophyL: trophyJ[0]!.p, trophyR: trophyJ[1]!.p, armR: armJ[1]!.p, foreR: foreJ[1]!.p, sword: swordJ.p,
    legL: legJ[0]!.p, shinL: shinJ[0]!.p, legR: legJ[1]!.p, shinR: shinJ[1]!.p,
  }));
};
